import {
  addEnabledJurisdiction,
  AVAILABLE_COUNTRIES_STATE_KEY,
  getAvailableCountries,
  getEnabledJurisdictions,
  getOrganizations,
  getOrganizationUsers,
  getOrganizationSettings,
  getOrgDetails,
  ORGANIZATIONS_STATE_KEY,
  createPhysicalMailOnboardingLink,
  removeEnabledJurisdiction,
  updateOrganizationAdminFields,
  updateOrganizationDetails,
  updateOrganizationSettings,
} from '@/apis/organizations-apis';
import { toaster } from '@/components/ui/toaster-instance';
import {
  CountryOption,
  EnabledJurisdictionsResponse,
  OrganizationAdminUpdate,
  OrganizationDetailsUpdate,
  OrganizationSettingUpdate,
} from '@/schemas';
import { getApiErrorMessage } from '@/utils/api';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useGetOrgDetailsQuery = (orgId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'details'],
    queryFn: async () => {
      const res = await getOrgDetails(orgId);
      return res?.data || null;
    },
    enabled: enabled && !!orgId,
  });
};

export const useGetOrgDetailsQueryWithReveal = (orgId: string, reveal: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'details', 'reveal', reveal],
    queryFn: async () => {
      const res = await getOrgDetails(orgId, reveal);
      return res?.data || null;
    },
    enabled: enabled && !!orgId,
  });
};

export const useGetOrganizationByIdQuery = (orgId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'byId'],
    queryFn: async () => {
      const res = await getOrganizations({
        search_query: orgId,
      });
      const organizations = res?.data?.items || [];
      return organizations.find(org => org.id === orgId) || null;
    },
    enabled,
  });
};

export const useGetOrganizationUsersQuery = (orgId: string) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'users'],
    queryFn: async () => {
      const response = await getOrganizationUsers(orgId);
      return response.data.users;
    },
    enabled: Boolean(orgId),
  });
};

export const useCreatePhysicalMailOnboardingMutation = (orgId: string) => {
  return useMutation({
    mutationFn: (customerEmail?: string) => createPhysicalMailOnboardingLink(orgId, customerEmail),
  });
};

export const useGetOrganizationSettingsQuery = (orgId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'settings'],
    queryFn: async () => {
      const res = await getOrganizationSettings(orgId);
      return res?.data || null;
    },
    enabled,
  });
};

export const useEnabledJurisdictionsQuery = (
  orgId: string,
  options?: Partial<UseQueryOptions<EnabledJurisdictionsResponse | null, Error>>
) => {
  return useQuery({
    queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'enabled-jurisdictions'],
    queryFn: async () => {
      const res = await getEnabledJurisdictions(orgId);
      return res?.data || null;
    },
    enabled: !!orgId,
    ...options,
  });
};

export const useGetAvailableCountriesQuery = (options?: Partial<UseQueryOptions<CountryOption[], Error>>) => {
  return useQuery({
    queryKey: [AVAILABLE_COUNTRIES_STATE_KEY],
    queryFn: async () => {
      const res = await getAvailableCountries();
      return res.data.countries;
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour - this data rarely changes
    ...options,
  });
};

export const useUpdateEnabledJurisdictionMutation = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, enabled }: { code: string; enabled: boolean }) => {
      return enabled ? addEnabledJurisdiction(orgId, code) : removeEnabledJurisdiction(orgId, code);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'enabled-jurisdictions'],
      });
      toaster.create({
        title: 'Success',
        description: 'Enabled jurisdictions updated successfully',
        type: 'success',
      });
    },
    onError: (error: unknown) => {
      const detail =
        typeof error === 'object' && error && 'response' in error
          ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      toaster.create({
        title: 'Error',
        description: detail || 'Failed to update enabled jurisdictions',
        type: 'error',
      });
    },
  });
};

export const useUpdateOrganizationSettingsMutation = (orgId: string, { silent = false } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationSettingUpdate) => updateOrganizationSettings(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'settings'],
      });
      if (!silent) {
        toaster.create({
          title: 'Success',
          description: 'Organization settings updated successfully',
          type: 'success',
        });
      }
    },
    onError: (error: unknown) => {
      if (!silent) {
        toaster.create({
          title: 'Error',
          description: getApiErrorMessage(error, 'Failed to update organization settings'),
          type: 'error',
        });
      }
    },
  });
};

export const useUpdateOrganizationDetailsMutation = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationDetailsUpdate) => updateOrganizationDetails(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_STATE_KEY, orgId, 'details'],
      });
      toaster.create({
        title: 'Success',
        description: 'Organization details updated successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      toaster.create({
        title: 'Error',
        description: error?.response?.data?.detail || 'Failed to update organization details',
        type: 'error',
      });
    },
  });
};

export const useUpdateOrganizationAdminFieldsMutation = (orgId: string, { silent = false } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationAdminUpdate) => updateOrganizationAdminFields(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_STATE_KEY, orgId],
      });
      if (!silent) {
        toaster.create({
          title: 'Success',
          description: 'Organization fields updated successfully',
          type: 'success',
        });
      }
    },
    onError: (error: unknown) => {
      if (!silent) {
        toaster.create({
          title: 'Error',
          description: getApiErrorMessage(error, 'Failed to update organization fields'),
          type: 'error',
        });
      }
    },
  });
};
